import { CollectionApiService } from '@aipb/gulliver/personal-content/data-access';
import {
  Message,
  MessageCollection,
  MessageEventType,
  MessageOrigin,
  MessagePriority,
  MessageSection,
  MessageText
} from '@aipb/gulliver/personal-content/model';
import { PersonalContentRouting } from '@aipb/gulliver/personal-content/routing';
import { SnackbarService } from '@aipb/shared/util/snackbar';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { formatDistance } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';
import { MatIconModule } from '@angular/material/icon';

const EVENT_ICONS: { [key in MessageEventType]: string } = {
  adminClear: 'shield_person',
  collectionCreate: 'library_add',
  collectionUpdate: 'edit_square',
  collectionRemove: 'delete',
  collectionDisseminate: 'send',
  collectionDisseminateManifest: 'history_edu',
  collectionCoopOpen: 'check',
  collectionCoopClose: 'close',
  nibbleCreate: 'add',
  nibbleUpdate: 'edit',
  nibbleCopy: 'content_copy',
  personalMessageSend: 'mail',
  collectionWithdraw: 'shield_person',
  nibbleRemoveFromCollectionById: 'delete',
  nibbleRemoveFromCollectionByServiceValue: 'delete',
  nibbleMove: 'open_with',
  nibbleEditCollRelatedNote: 'rate_review',
  nibbleEditCollRelatedComment: 'rate_review',
  collectionCoopJoin: 'diversity_3',
  nibbleChangePosition: 'swap_vert',
  collectionWithdrawManifest: 'history_edu'
};

const PRIORITY_COLOR_CLASSES: { [key in MessagePriority]: string } = {
  medium: 'text-orange-500'
};

const ORIGIN_ICONS: { [key in MessageOrigin]: string } = {
  systemGenerated: 'computer',
  userGenerated: 'user'
};

function getByKey<T extends symbol | string, V>(key: T, data: { [key in T]: V }, defaultValue: V): V {
  if (key in data) {
    return data[key];
  }

  return defaultValue;
}

const DEFAULT_ICON = 'question_mark';

@UntilDestroy()
@Component({
  selector: 'pc-message-list',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslocoModule, MatButtonModule],
  templateUrl: './message-list.component.html',
  styleUrls: ['./message-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessageListComponent implements OnChanges {
  @Input() lang: string;
  @Input() compact = false;
  @Input() messages: Message[] = [];

  @Output() sendMessageToUser = new EventEmitter<string>();

  private personalContentRouting = inject(PersonalContentRouting);
  private collectionApi = inject(CollectionApiService);
  private snackbar = inject(SnackbarService);

  _sections: MessageSection[] = [];

  get sections() {
    return this._sections;
  }

  ngOnChanges() {
    const sections: {
      [key: string]: Message[];
    } = {};

    this.messages.forEach((message) => {
      const date = formatDistance(new Date(message.structured.datetime), new Date(), {
        addSuffix: true,
        locale: this.lang === 'cs' ? cs : enUS
      });

      if (date in sections) {
        sections[date].push(message);
      } else {
        sections[date] = [message];
      }
    });

    this._sections = Object.keys(sections).map((date) => ({
      date,
      messages: sections[date]
    }));
  }

  getText(text: MessageText) {
    if (this.lang in text) {
      return text[this.lang];
    }

    return '';
  }

  getPriorityColorClass(priority: MessagePriority) {
    return getByKey(priority, PRIORITY_COLOR_CLASSES, '');
  }

  getOriginIcon(origin: MessageOrigin) {
    return getByKey(origin, ORIGIN_ICONS, DEFAULT_ICON);
  }

  hasEventIcon(event: MessageEventType) {
    return this.getEventIcon(event) !== DEFAULT_ICON;
  }

  getEventIcon(event: MessageEventType) {
    return getByKey(event, EVENT_ICONS, DEFAULT_ICON);
  }

  onRouteToCollection(collection: MessageCollection) {
    if (this.isFavorites(collection) || this.isTrashbin(collection)) return;

    this.collectionApi
      .get({
        id: collection.uniqueId
      })
      .subscribe({
        next: () => {
          this.personalContentRouting.collectionSingle(collection.uniqueId);
        },
        error: (err) => {
          this.snackbar.open('personal_content.messages.collection_delete_by_owner', 'error');
        }
      });
  }

  hasLink(collection: MessageCollection) {
    return !this.isFavorites(collection) && !this.isTrashbin(collection);
  }

  isFavorites(collection: MessageCollection) {
    return collection.label === '⭐️';
  }

  isTrashbin(collection: MessageCollection) {
    return collection.label === '🗑️';
  }
}
